import { useEffect, useState } from 'react';
import { ApiError, get, type RundownItemDto, type StoryDto } from '../api.js';
import { t } from '../i18n.js';
import { fmtDateTime, hhmmss, mmss } from '../util.js';

export interface MediaRow {
  id: string;
  title: string;
  filename: string;
  durationMs: number;
  status: string;
  version: string;
  lastSeenAt: string;
}

interface Graphic {
  id: string;
  position: number;
  slug: string;
  templateId: string | null;
  mosObjId: string | null;
  channel: string | null;
  readiness: string | null;
}

interface Props {
  /** The rundown row under the cursor; null when the rundown is empty. */
  item: RundownItemDto | null;
  canEdit: boolean;
  /** Link a playout clip to the row (null removes the link). Resolves when the rundown has accepted it. */
  onAssign: (mediaAssetId: string | null) => Promise<boolean>;
  /** Open the graphics editor for the row. */
  onGraphics: () => void;
  /** Bumped by the grid when the graphics dialog closes, so the list reloads. */
  graphicsVersion: number;
  onClose: () => void;
}

const statusClass = (s: string) => (s === 'READY' ? 'ok' : s === 'MISSING' ? 'err' : 'warn');

/**
 * The media side panel of the rundown: for the row under the cursor, the playout clip that will play
 * (choose one from the playout's library and send it with PLAYOUT), the row's graphics, and the script.
 * Everything shown comes from the newsroom's mirror of the playout library and the rundown itself.
 */
export function MediaPanel({ item, canEdit, onAssign, onGraphics, graphicsVersion, onClose }: Props) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<MediaRow[]>([]);
  const [currentRow, setCurrentRow] = useState<MediaRow | null>(null);
  const [graphicsOf, setGraphicsOf] = useState<{ itemId: string; list: Graphic[] } | null>(null);
  const [storyOf, setStoryOf] = useState<{ storyId: string; story: StoryDto } | null>(null);
  const [showTextFor, setShowTextFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // library search, debounced; the empty query lists the newest clips
  useEffect(() => {
    const h = window.setTimeout(() => {
      get<{ media: MediaRow[] }>(`/api/media?q=${encodeURIComponent(q.trim())}&limit=40`)
        .then((r) => setRows(r.media))
        .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    }, 200);
    return () => window.clearTimeout(h);
  }, [q]);

  // the clip linked to the row
  const mediaId = item?.mediaAssetId ?? null;
  useEffect(() => {
    if (!mediaId) return;
    let cancelled = false;
    get<{ media: MediaRow[] }>(`/api/media?ids=${encodeURIComponent(mediaId)}&limit=1`)
      .then((r) => !cancelled && setCurrentRow(r.media[0] ?? null))
      .catch(() => !cancelled && setCurrentRow(null));
    return () => {
      cancelled = true;
    };
  }, [mediaId]);

  // graphics of the row
  const itemId = item?.id ?? null;
  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    get<{ graphics: Graphic[] }>(`/api/items/${itemId}/graphics`)
      .then((r) => !cancelled && setGraphicsOf({ itemId, list: r.graphics }))
      .catch(() => !cancelled && setGraphicsOf({ itemId, list: [] }));
    return () => {
      cancelled = true;
    };
  }, [itemId, graphicsVersion]);

  // the script behind the row
  const storyId = item?.storyId ?? null;
  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    get<{ story: StoryDto }>(`/api/stories/${storyId}`)
      .then((r) => !cancelled && setStoryOf({ storyId, story: r.story }))
      .catch(() => !cancelled && setStoryOf(null));
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  const assign = async (id: string | null) => {
    setBusy(id ?? 'clear');
    setError(null);
    try {
      await onAssign(id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const canLink = canEdit && !!item?.storyId;
  // derived from the row under the cursor, so a stale fetch for another row never shows
  const current = mediaId && currentRow?.id === mediaId ? currentRow : null;
  const graphics = graphicsOf?.itemId === itemId ? graphicsOf.list : [];
  const story = storyId && storyOf?.storyId === storyId ? storyOf.story : null;
  const showText = showTextFor === storyId;

  return (
    <aside className="media-panel" aria-label={t('mediaPanel')}>
      <div className="media-panel-head">
        <b>{t('mediaPanel')}</b>
        <button type="button" className="media-panel-close" onClick={onClose} aria-label={t('close')} title={t('close')}>
          ✕
        </button>
      </div>
      {!item ? (
        <p className="muted media-empty">{t('mediaPanelNoRow')}</p>
      ) : (
        <>
          <div className="media-row-title">
            <span className="num">{item.displayNumber ?? '-'}</span> {item.title}
            <div className="muted small">
              {item.kind} · {t('colVideo')} {mmss(item.videoS)} · {t('colRead')} {mmss(item.readS)}
            </div>
          </div>
          {error && <div className="banner err">{error}</div>}

          <section className="media-sect">
            <h4>
              {t('colMedia')} {current ? <span className="status ok">{t('mediaOnRow')}</span> : <span className="status warn">—</span>}
            </h4>
            {!item.storyId && <p className="muted small">{t('mediaPanelNoStory')}</p>}
            {!current && item.mediaCode && (
              <div className="media-card virtual">
                <div className="media-thumb" aria-hidden="true">
                  ⌛
                </div>
                <div className="media-card-body">
                  <div className="media-card-title">{item.mediaCode}</div>
                  <div className="muted small">{t('virtualWaiting')}</div>
                </div>
                <span className="status warn">{t('virtualBadge')}</span>
              </div>
            )}
            {current && (
              <MediaCard
                m={current}
                linked
                busy={busy === 'clear'}
                canLink={canLink}
                action={t('mediaClear')}
                onAction={() => void assign(null)}
              />
            )}
            {item.storyId && (
              <>
                <input type="search" className="media-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mediaSearch')} />
                <div className="media-list">
                  {rows.filter((m) => m.id !== mediaId).length === 0 && <p className="muted small">{t('mediaNone')}</p>}
                  {rows
                    .filter((m) => m.id !== mediaId)
                    .map((m) => (
                      <MediaCard key={m.id} m={m} busy={busy === m.id} canLink={canLink} action={`▶ ${t('mediaToPlayout')}`} onAction={() => void assign(m.id)} />
                    ))}
                </div>
              </>
            )}
          </section>

          <section className="media-sect">
            <h4>
              {t('colGraphics')} <span className="muted">{graphics.length}</span>
              {item.storyId && canEdit && (
                <button type="button" className="btn small" onClick={onGraphics} title={t('graphicsAdd')}>
                  ＋
                </button>
              )}
            </h4>
            {graphics.length === 0 && <p className="muted small">—</p>}
            {graphics.map((g) => (
              <div key={g.id} className="media-card graphic">
                <div className="media-thumb gfx" aria-hidden="true">
                  Aa
                </div>
                <div className="media-card-body">
                  <div className="media-card-title">{g.slug || g.templateId || g.mosObjId || '—'}</div>
                  <div className="muted small">
                    {g.templateId ?? ''} {g.channel ? `· ${g.channel}` : ''}
                  </div>
                </div>
                <span className={`status ${g.readiness === 'READY' ? 'ok' : g.readiness ? 'warn' : 'info'}`}>{g.readiness ?? t('graphicsNotReported')}</span>
              </div>
            ))}
          </section>

          {story && (
            <section className="media-sect">
              <h4>
                <button type="button" className="linklike" onClick={() => setShowTextFor(showText ? null : storyId)}>
                  {showText ? '▾' : '▸'} {t('storyText')} <span className="muted">{story.wordCount} {t('words')}</span>
                </button>
              </h4>
              {showText && <div className="media-story-text">{story.bodyText || '—'}</div>}
            </section>
          )}
        </>
      )}
    </aside>
  );
}

function MediaCard({ m, linked, busy, canLink, action, onAction }: { m: MediaRow; linked?: boolean; busy: boolean; canLink: boolean; action: string; onAction: () => void }) {
  const [thumbOk, setThumbOk] = useState(true);
  return (
    <div className={`media-card${linked ? ' linked' : ''}`}>
      {thumbOk ? (
        <img className="media-thumb" src={`/api/media/${encodeURIComponent(m.id)}/thumb?v=${encodeURIComponent(m.version)}`} alt="" loading="lazy" onError={() => setThumbOk(false)} />
      ) : (
        <div className="media-thumb" aria-hidden="true">
          🎬
        </div>
      )}
      <div className="media-card-body">
        <div className="media-card-title" title={m.filename}>
          {m.title}
        </div>
        <div className="muted small">
          {hhmmss(Math.round(m.durationMs / 1000))} · <span className={`status ${statusClass(m.status)}`}>{m.status}</span>
        </div>
        <div className="muted small">{fmtDateTime(m.lastSeenAt)}</div>
      </div>
      <button type="button" className={`btn small${linked ? ' danger' : ' primary'}`} disabled={busy || !canLink} onClick={onAction}>
        {action}
      </button>
    </div>
  );
}
